import type { ReactElement } from 'react';

import { CardFrame, CardNote, QueryBody, RANGE_NOTE } from '@/components/ui/card-frame';
import { cn } from '@/components/ui/cn';
import { ExportMenu } from '@/components/ui/ExportMenu';

import {
  GRANULARITY_LABELS,
  periodLabel,
  periodPhrase,
  throughputProblem,
  throughputQuery,
} from '../presentation';
import {
  GRANULARITIES,
  THROUGHPUT_SERIES,
  type Granularity,
  type PeriodPick,
  type Throughput,
  type ThroughputSeries,
  type TimeStudyFilters,
} from '../types';
import { usePeriodParam } from '../use-period-param';
import { useThroughput } from '../use-time-study';
import { BarChart } from './BarChart';
import { ThroughputDocumentsModal } from './ThroughputDocumentsModal';

// Documentos por período (so=420, spec §4.2 tarjeta 6 y D2): terminados en vigencia e
// iniciados por día, mes o año, con la tabla equivalente del gráfico. Cada barra (con
// el mouse) y cada cifra de la tabla (con teclado) abren los documentos del período
// (G1-7); el período abierto vive en la URL.

interface Props {
  filters: TimeStudyFilters;
  companyId: string;
  onGranularityChange: (g: Granularity) => void;
}

const PER: Readonly<Record<Granularity, string>> = { day: 'día', month: 'mes', year: 'año' };
const SERIES = [
  { key: 'completed', name: 'Terminados', tone: 's1' },
  { key: 'started', name: 'Iniciados', tone: 's2' },
] as const;
const NOUN: Readonly<Record<ThroughputSeries, [string, string]>> = {
  completed: ['terminado en vigencia', 'terminados en vigencia'],
  started: ['iniciado', 'iniciados'],
};

const isSeries = (k: string): k is ThroughputSeries => THROUGHPUT_SERIES.some((s) => s === k);

function pickLabel(n: number, series: ThroughputSeries, phrase: string): string {
  const [one, many] = NOUN[series];
  return n === 1
    ? `Ver el documento ${one} ${phrase}`
    : `Ver los ${n} documentos ${many} ${phrase}`;
}

function CountCell(props: {
  n: number;
  series: ThroughputSeries;
  period: string;
  gran: Granularity;
  onPick: (p: PeriodPick) => void;
}): ReactElement {
  const { n, series, period, gran, onPick } = props;
  return (
    <td className="dim-mono">
      {n === 0 ? (
        n
      ) : (
        <button
          type="button"
          className="rowpick cnt-pick"
          aria-label={pickLabel(n, series, periodPhrase(period, gran))}
          onClick={() => onPick({ period, series })}
        >
          {n}
        </button>
      )}
    </td>
  );
}

function ThroughputBody({
  data,
  onPick,
}: {
  data: Throughput;
  onPick: (p: PeriodPick) => void;
}): ReactElement {
  const per = PER[data.granularity];
  return (
    <div className="cb">
      <BarChart
        label={`Documentos por ${per}: ${data.totals.completed} terminados y ${data.totals.started} iniciados en el rango`}
        series={SERIES}
        groups={data.buckets.map((b) => ({
          key: b.period,
          label: periodLabel(b.period, data.granularity),
          values: [b.completed, b.started],
        }))}
        onPick={(period, series) => isSeries(series) && onPick({ period, series })}
        barLabel={(period, series, n) =>
          isSeries(series) ? pickLabel(n, series, periodPhrase(period, data.granularity)) : ''
        }
      />
      <details className="chartdata">
        <summary>Ver los datos</summary>
        <div className="t-scroll">
          <table className="t">
            <caption className="sr-only">{`Documentos terminados e iniciados por ${per}`}</caption>
            <thead>
              <tr>
                <th scope="col">Período</th>
                <th scope="col">Terminados</th>
                <th scope="col">Iniciados</th>
              </tr>
            </thead>
            <tbody>
              {data.buckets.map((b) => (
                <tr key={b.period}>
                  <th scope="row">{periodLabel(b.period, data.granularity)}</th>
                  {SERIES.map((s) => (
                    <CountCell
                      key={s.key}
                      n={b[s.key]}
                      series={s.key}
                      period={b.period}
                      gran={data.granularity}
                      onPick={onPick}
                    />
                  ))}
                </tr>
              ))}
              <tr>
                <th scope="row">Total</th>
                <td className="dim-mono">{data.totals.completed}</td>
                <td className="dim-mono">{data.totals.started}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

export function ThroughputCard({ filters, companyId, onGranularityChange }: Props): ReactElement {
  // Tope propio por granularidad (92 días, 36 meses, 10 años), no el de 366 días del
  // análisis: por mes o año tiene sentido un rango de varios años (so=420 M4).
  // El rango invertido ya se explica bajo los filtros: aquí solo la nota breve.
  const problem = filters.desde > filters.hasta ? RANGE_NOTE : throughputProblem(filters);
  const query = useThroughput(filters, { companyId, enabled: problem === null });
  const [pick, setPick] = usePeriodParam();
  // Solo se abre un período del gráfico vigente (mismo rango, filtros y granularidad).
  const open =
    problem === null &&
    pick !== null &&
    query.data?.granularity === filters.gran &&
    query.data.buckets.some((b) => b.period === pick.period)
      ? pick
      : null;
  return (
    <CardFrame
      title="Documentos por período"
      count="terminados en vigencia e iniciados"
      actions={
        problem === null && (
          <ExportMenu
            compact
            path="/api/v1/quality/productivity/throughput/export"
            query={throughputQuery(filters)}
            title="Documentos por período"
          />
        )
      }
    >
      <div className="cb" style={{ paddingBottom: 0 }}>
        <div className="seg" role="group" aria-label="Agrupar por período">
          {GRANULARITIES.map((g) => (
            <button
              key={g}
              type="button"
              aria-pressed={g === filters.gran}
              className={cn(g === filters.gran && 'on')}
              onClick={() => onGranularityChange(g)}
            >
              {GRANULARITY_LABELS[g]}
            </button>
          ))}
        </div>
      </div>
      {problem !== null ? (
        <CardNote>{problem}</CardNote>
      ) : (
        <QueryBody query={query} rows={4}>
          {(d) => <ThroughputBody data={d} onPick={setPick} />}
        </QueryBody>
      )}
      {open !== null && (
        <ThroughputDocumentsModal
          filters={filters}
          pick={open}
          companyId={companyId}
          onClose={() => setPick(null)}
        />
      )}
    </CardFrame>
  );
}
